import URI from '@dw/urijs-esm/urijs-esm.js';
import { store } from '../../store.js';
import * as app from '../app/index.js';
import * as feature from '../feature/index.js';
import * as InAppBrowser from '../cordova-plugins/in-app-browser.js';

const WINDOW_WIDTH_THRESHOLD = 767;
const MOBILE_CHILD_WINDOW_WIDTH = 400;
const MOBILE_CHILD_WINDOW_HEIGHT = 500;
const DESKTOP_WINDOW_WIDTH = 1200;
const DESKTOP_WINDOW_HEIGHT = 800;
const CHECK_INTERVAL = 1000;
const CHILD_WINDOW_FEATURES = "titlebar=no, toolbar=no, menubar=no, scrollbars=yes, resizable=no, location=no, directories=no, status=no";

/**
 * Feature that decides whether a plan may be bought or changed from inside the installed
 * app. Absent from the config it reads as available, so it only ever needs setting to
 * turn the flow off again.
 */
const CHANGE_PLAN_FEATURE = 'installed-app-change-plan';

/**
 * The page Stripe sends the person back to. It carries the outcome in its own query, which
 * is how the installed app learns what happened without a message from a window it cannot
 * hear.
 */
const FALLBACK_PAGE = 'checkout-session-fallback.html';

/**
 * Back and forward have nothing to offer on a payment page and only send the person
 * backwards through Stripe, so they go. The close button stays: it is the only way out,
 * and closing early is what tells us the person walked away.
 */
const BROWSER_OPTIONS = 'location=no,hidenavigationbuttons=yes,toolbarcolor=#ffffff';

/**
 * The url Stripe is opened with carries the fallback page in its own `redirect` query, so
 * looking for the name anywhere in the url matches that first navigation too. Only the page
 * itself counts.
 */
const isFallbackPage = (url) => {
  try {
    return new URI(url).path().endsWith(`/${FALLBACK_PAGE}`);
  } catch (e) {
    return false;
  }
};

export const checkoutSession = ({ accountId, priceId, seats, inSameWindow, additionalSeats = 0, autoRenew = false, wideLayout = false, email, promotionCode }) => {
  const params = { accountId, priceId, seats, inSameWindow, additionalSeats, autoRenew, wideLayout, email, promotionCode };
  const state = store.getState();

  if (!inSameWindow
    && app.selectors.isInstalledApp(state)
    && feature.selectors.isAvailable(state, CHANGE_PLAN_FEATURE)) {
    return checkoutSessionInAppBrowser(params);
  }

  return checkoutSessionInChildWindow(params);
};

/**
 * The installed app's web view opens nothing outside the PWA domain, and a child window it
 * did open could not post back to us. Cordova's in-app browser opens Stripe instead, and the
 * page Stripe returns to is one of ours: its url says how it went, so watching where the
 * browser goes takes the place of the message.
 */
const checkoutSessionInAppBrowser = async (params) => {
  const redirectURL = getCheckoutSessionUrl(params);
  const browser = await InAppBrowser.open(redirectURL, '_blank', BROWSER_OPTIONS);

  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = (fn, value) => {
      if (settled) {
        return;
      }
      settled = true;
      fn(value);
    };

    browser.addEventListener('loadstart', (e) => {
      const url = (e && e.url) || '';
      if (!isFallbackPage(url)) {
        return;
      }

      const search = new URI(url).search(true);
      browser.close();

      if (search.status === 'SUCCESS') {
        settle(resolve, { type: 'CHANGE_PLAN_SUCCESS', accountId: params.accountId, status: search.status });
        return;
      }

      // Only a stated failure is a failure. Stripe's own back button, and anything else that
      // returns without saying how it went, is someone changing their mind.
      if (search.status === 'FAILED') {
        settle(reject, { code: search.error || search.status });
        return;
      }

      settle(reject, { code: 'CANCELED' });
    });

    // Closed before Stripe sent anyone back - the person walked away.
    browser.addEventListener('exit', () => settle(reject, { code: 'CANCELED' }));
  });
};

let __childWindow;

const checkoutSessionInChildWindow = ({ accountId, priceId, seats, inSameWindow, additionalSeats, autoRenew, wideLayout, email, promotionCode }) => {
  const redirectURL = getCheckoutSessionUrl({ accountId, priceId, seats, inSameWindow, additionalSeats, autoRenew, email, promotionCode });

  if (inSameWindow) {
    window.location.href = redirectURL;
    return;
  }

  const { width, height, left, top } = getChildWindowDimensions(wideLayout);
  __childWindow && __childWindow.close && __childWindow.close();
  __childWindow = window.open(redirectURL, "", `${CHILD_WINDOW_FEATURES}, width=${width}, height=${height}, left=${left}, top=${top}`);

  return new Promise((resolve, reject) => {
    const timer = setInterval(() => checkChildWindow(timer, reject), CHECK_INTERVAL);
    window.addEventListener('message', (e) => handleWindowMessage(e, timer, resolve, reject));
  });
};

const getChildWindowDimensions = (wideLayout) => {
  const CHILD_WINDOW_WIDTH = wideLayout ? DESKTOP_WINDOW_WIDTH : MOBILE_CHILD_WINDOW_WIDTH;
  const CHILD_WINDOW_HEIGHT = wideLayout ? DESKTOP_WINDOW_HEIGHT : MOBILE_CHILD_WINDOW_HEIGHT;
  const width = window.innerWidth > WINDOW_WIDTH_THRESHOLD ? CHILD_WINDOW_WIDTH : window.innerWidth;
  const height = window.innerWidth > WINDOW_WIDTH_THRESHOLD ? CHILD_WINDOW_HEIGHT : window.innerHeight;
  const left = window.innerWidth > WINDOW_WIDTH_THRESHOLD ? (screen.width - width) / 2 : 0;
  const top = window.innerWidth > WINDOW_WIDTH_THRESHOLD ? (screen.height - height) / 2 : 0;
  return { width, height, left, top };
};

const checkChildWindow = (timer, reject) => {
  if (!__childWindow || __childWindow.closed) {
    reject({ code: 'CANCELED' });
    clearInterval(timer);
  }
};

const handleWindowMessage = (e, timer, resolve, reject) => {
  const { type, error, status } = e.data || {};
  const errorCode = type === 'CHANGE_PLAN_CANCELED' ? 'CANCELED' : error || status || 'UNKNOWN';

  if (['CHANGE_PLAN_SUCCESS', 'CHANGE_PLAN_FAILED', 'CHANGE_PLAN_CANCELED'].includes(type)) {
    clearInterval(timer);
    window.removeEventListener('message', handleWindowMessage);
    type === 'CHANGE_PLAN_SUCCESS' ? resolve(e.data) : reject({ code: errorCode });
  }
};

const getCheckoutSessionUrl = ({ accountId, priceId, seats, inSameWindow, additionalSeats, autoRenew, email, promotionCode }) => {
  const uri = new URI(`${window.K.config.apiBaseUrl}/subscription-v4/subscriptions/${accountId}/start-subscription`);
  if (priceId) uri.setSearch('price-id', priceId);
  if (seats) uri.setSearch('seats', seats);
  if (additionalSeats) uri.setSearch('additional-seats', additionalSeats);
  if (email) uri.setSearch('email', encodeURIComponent(email));
  if (promotionCode) uri.setSearch('promotion-code', promotionCode);
  uri.setSearch('auto-renew', autoRenew);

  const redirect = getRedirectUrl({ accountId, priceId, seats, inSameWindow, additionalSeats, autoRenew, email, promotionCode });
  if (redirect) uri.setSearch('redirect', redirect);

  return uri.toString();
};

const getRedirectUrl = ({ accountId, priceId, seats, inSameWindow, additionalSeats, autoRenew, email, promotionCode }) => {
  if (!inSameWindow) {
    const uri = new URI(getFallbackPageUrl());
    if (accountId) uri.setSearch('account-id', accountId);
    if (priceId) uri.setSearch('price-id', priceId);
    if (seats) uri.setSearch('seats', seats);
    if (additionalSeats) uri.setSearch('additional-seats', additionalSeats);
    if (email) uri.setSearch('email', encodeURIComponent(email));
    if (promotionCode) uri.setSearch('promotion-code', promotionCode);
    uri.setSearch('auto-renew', autoRenew);
    return uri.toString();
  }

  const sameWindowUri = new URI();
  sameWindowUri.setSearch('checkout-session-in-same-window', true);
  sameWindowUri.removeSearch('status');
  sameWindowUri.removeSearch('error');
  return sameWindowUri.toString();
};

const getFallbackPageUrl = () => {
  return `${window.location.protocol}//${window.location.host}/checkout-session-fallback.html`;
};

export default checkoutSession;
